import type { AwbTrackingData } from '@aircargomcp/types'
import { type FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { type ApiError, trackAwbPublic } from '../lib/api'
import { SAMPLES, lookupSample } from '../lib/samples'
import { AwbTimeline } from './AwbTimeline'
import { screenAwbOf } from './air-shipment/shipment-url'

/** The waybill a shared link names (`?awb=`), with or without its dash; null when there is none. */
export function awbFromLink(search: string): string | null {
  return screenAwbOf(search)
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; data: AwbTrackingData }
  | { kind: 'error'; error: ApiError }

// Friendly copy per error code (the API returns the machine codes from packages/types AppError).
// `cta` is the one action that resolves the error, when there is one. Exported so TrackBox.test.ts can
// hold the free-limit wording to its promise.
export type ErrorCopy = { head: string; body: string; cta?: { to: string; label: string } }
export function errorCopy(e: ApiError): ErrorCopy {
  switch (e.code) {
    // ONLY `gated` means the free web tier is spent (per-IP hourly cap or the shared daily budget). It is
    // not an outage, so say what happened and where the unlimited path starts. `rate_limited` is NOT this:
    // it is an upstream source throttling us, and a keyed call would fail the same way — it stays generic.
    case 'gated':
      return {
        head: 'Free tracking limit reached for now',
        body: 'The free web tracker allows a limited number of lookups per hour and per day. Get an API key for unlimited AWB tracking, or try again later.',
        cta: { to: '/contact', label: 'Get an API key →' },
      }
    case 'invalid_reference':
      return {
        head: 'That doesn’t look like a valid AWB',
        body: 'An air waybill is 11 digits: a 3-digit airline prefix plus an 8-digit serial, e.g. 203-12345675.',
      }
    case 'unsupported_carrier':
      return { head: 'Airline not wired yet', body: e.message }
    case 'not_found':
      return {
        head: 'No shipment found',
        body: 'The airline returned no record for that AWB. Double-check the number.',
      }
    default:
      return {
        head: 'Tracking is briefly unavailable',
        body: e.message || 'Please try again in a moment.',
      }
  }
}

function ErrorCard({ error }: { error: ApiError }) {
  const copy = errorCopy(error)
  return (
    <div className="reveal card mt-5 border-l-2 border-l-down p-5">
      <div className="font-semibold text-ink">{copy.head}</div>
      <p className="mt-1 text-sm text-muted">{copy.body}</p>
      {copy.cta && (
        <Link
          to={copy.cta.to}
          className="mt-3 inline-block text-sm font-semibold text-signal hover:underline"
        >
          {copy.cta.label}
        </Link>
      )}
    </div>
  )
}

export function TrackBox() {
  const [awb, setAwb] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })

  // A shared link (`?awb=`) fills the form and tracks its waybill on arrival.
  useEffect(() => {
    const linked = awbFromLink(window.location.search)
    if (!linked) return
    setAwb(linked)
    void track(linked)
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!awb.trim()) return
    await track(awb)
  }

  async function track(awb: string) {
    // Demo AWBs resolve to hardcoded fixtures — no API key, no network — so anonymous
    // visitors always see a full timeline. Real AWBs fall through to the live API.
    const sample = lookupSample(awb)
    if (sample) {
      setState({ kind: 'result', data: sample })
      return
    }
    setState({ kind: 'loading' })
    // The FREE public tier (GET /track/public/:awb): this form is for visitors without a key. The keyed
    // /track/:awb answers 401 to them, which is what every real AWB typed here used to get. Same call the
    // Workspace embed makes (WorkspaceAir.tsx).
    const res = await trackAwbPublic(awb)
    setState(res.ok ? { kind: 'result', data: res.data } : { kind: 'error', error: res.error })
  }

  return (
    <div>
      <form
        onSubmit={onSubmit}
        className="card flex flex-col items-stretch gap-1 rounded-2xl p-1.5 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center px-4 py-2">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="mr-3 shrink-0 text-faint"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" strokeLinecap="round" />
          </svg>
          <div className="min-w-0 flex-1">
            <div className="mono text-[10px] uppercase tracking-wider text-faint">
              Air waybill number
            </div>
            <input
              value={awb}
              onChange={(e) => setAwb(e.target.value)}
              placeholder="203-12345675"
              spellCheck={false}
              autoComplete="off"
              inputMode="numeric"
              className="mono w-full bg-transparent text-[16px] font-semibold text-ink outline-none placeholder:font-normal placeholder:text-faint"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={state.kind === 'loading' || !awb.trim()}
          className="btn-accent shrink-0 rounded-xl px-6 py-3 text-center font-semibold disabled:cursor-not-allowed disabled:opacity-45"
        >
          {state.kind === 'loading' ? 'Tracking…' : 'Track'}
        </button>
      </form>

      <div className="mt-2 flex flex-col gap-1.5 text-xs text-muted">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="mono uppercase tracking-wider text-faint">Try</span>
          {SAMPLES.map((s) => (
            <button
              key={s.awb}
              type="button"
              onClick={() => {
                setAwb(s.awb)
                setState({ kind: 'result', data: s.data })
              }}
              title={s.label}
              className="mono rounded-full border border-border bg-surface-0 px-2.5 py-1 text-muted transition-colors hover:border-signal hover:text-ink"
            >
              {s.awb}
            </button>
          ))}
        </div>
        <span className="text-faint">
          Sample shipments · full Cargo-IMP timeline, no API key needed
        </span>
      </div>

      {state.kind === 'result' && (
        <div className="reveal mt-5">
          <AwbTimeline data={state.data} />
        </div>
      )}
      {state.kind === 'error' && <ErrorCard error={state.error} />}
    </div>
  )
}
